import { useEffect } from "react";
import { getSessionId } from "@/config/sessionConfig";
import { logTelemetry } from "@/utils/LogHelper";
import Logger from "@/utils/Logger";

/**
 * Custom hook for tracking page views ONCE per session
 *
 * This prevents excessive telemetry logging while still capturing
 * meaningful page view data. Uses sessionStorage to track which
 * pages have been logged in the current browser session.
 *
 * Logs ONLY ONCE per session (not per mount, not per render)
 * Session-scoped: New tab/window = new session = new log
 * Persists through page refreshes within the same tab
 *
 * @param pageName - Unique identifier for the page (e.g., "Search Page", "Admin Dashboard")
 * @param additionalData - Optional metadata to include with the page view
 *
 * @example
 * ```tsx
 * const SearchPage = () => {
 *   usePageTracking("Search Page", { feature: "item-search" });
 *   // ...rest of component
 * }
 * ```
 */
export const usePageTracking = (
  pageName: string,
  additionalData?: Record<string, unknown>,
): void => {
  const sessionId = getSessionId();

  useEffect(() => {
    // Guard: Require valid session ID
    if (!sessionId) {
      Logger.debug(
        "No session ID available, skipping page tracking telemetry",
        ["PageTracking"],
      );
      return;
    }

    // Create unique key for this page + session combination
    const pageViewKey = `page_viewed_${pageName.replace(/\s+/g, "_").toLowerCase()}_${sessionId}`;

    // Check if we've already logged this page view in this session
    const hasLoggedThisSession = sessionStorage.getItem(pageViewKey);

    if (!hasLoggedThisSession) {
      Logger.debug(`Tracking page view: ${pageName} (session: ${sessionId})`, [
        "PageTracking",
      ]);

      // Log page view with session context
      logTelemetry(pageName, {
        timestamp: new Date().toISOString(),
        sessionId,
        path: window.location.pathname,
        ...additionalData,
      });

      // Mark as logged for this session (prevents duplicate logs)
      sessionStorage.setItem(pageViewKey, "true");
    } else {
      Logger.debug(`Page view already tracked for session: ${pageName}`, [
        "PageTracking",
      ]);
    }
  }, [pageName, sessionId]); // Only re-run if page name or session changes
};

/**
 * Custom hook for tracking user actions/events (not page views)
 *
 * TODO: Implement user event tracking in future iterations
 * Unlike page tracking, this would allow multiple events per session
 * and is suitable for tracking button clicks, form submissions, etc.
 *
 * @returns Function to log telemetry events
 *
 * @example
 * ```tsx
 * const SearchPage = () => {
 *   const trackEvent = useEventTracking();
 *
 *   const handleSearch = () => {
 *     trackEvent("Search Executed", { searchType: "advanced" });
 *     // ...perform search
 *   }
 * }
 * ```
 */
export const useEventTracking = (): ((
  eventName: string,
  eventData?: Record<string, unknown>,
) => void) => {
  const sessionId = getSessionId();

  return (eventName: string, eventData?: Record<string, unknown>) => {
    if (!sessionId) {
      Logger.debug("No session ID available, skipping event tracking", [
        "EventTracking",
      ]);
      return;
    }

    Logger.debug(`Tracking event: ${eventName}`, ["EventTracking"]);

    // TODO: Implement event tracking with backend support
    logTelemetry(eventName, {
      timestamp: new Date().toISOString(),
      sessionId,
      path: window.location.pathname,
      ...eventData,
    });
  };
};
